import React, { useEffect, useMemo, useState } from 'react';
import { StyleProp, TextStyle } from 'react-native';
import Animated, {
    AnimatedStyle,
    runOnJS,
    useAnimatedStyle,
    useSharedValue,
    withTiming,
} from 'react-native-reanimated';

import { Text, TextProps } from '@/components/TextComponent';
import { Box, BoxProps } from '@/components/lib/components';
import { textVariants } from '@/components/lib/typography';
import { usePrevious } from '@/hooks/use-previous';

type AnimatedNumberProps = {
    value: number;
    decimals?: number;
    appendText?: string;
    prependText?: string;
    alignText?: BoxProps['alignItems'];
    displayFormat?: (value: number) => string | number;
    animate?: boolean;
    animatedTextStyle?: AnimatedStyle<StyleProp<TextStyle>>;
} & TextProps;

const AnimatedText = Animated.createAnimatedComponent(Text);

export const AnimatedNumber = React.memo(
    ({
        value,
        appendText,
        prependText,
        decimals = 2,
        alignText = 'flex-end',
        displayFormat = (val: number) => val.toFixed(decimals),
        animate = true,
        animatedTextStyle,
        ...textProps
    }: AnimatedNumberProps) => {
        const prevValue = usePrevious(value);
        const [displayValue, setDisplayValue] = useState(value);

        const lineHeight =
            (textProps.lineHeight as number) ??
            textVariants[(textProps.variant as keyof typeof textVariants) || 'bodyMedium'].lineHeight;

        const translateY = useSharedValue(0);

        const textAlignValue = alignText === 'flex-end' ? 'right' : 'left';

        useEffect(() => {
            if (prevValue === undefined || prevValue === value) {
                return;
            }

            if (!animate) {
                setDisplayValue(value);
                return;
            }

            const direction = Math.sign(value - prevValue);
            const offset = direction * lineHeight;

            // Slide out, update, slide in
            translateY.value = withTiming(offset, { duration: 150 }, () => {
                runOnJS(setDisplayValue)(value);
                translateY.value = -offset;
                translateY.value = withTiming(0, { duration: 150 });
            });
        }, [value, prevValue, animate, lineHeight, translateY]);

        const animatedTransformStyle = useAnimatedStyle(() => ({
            transform: [{ translateY: translateY.value }],
        }));

        const formattedDisplayValue = useMemo(() => displayFormat(displayValue), [displayValue, displayFormat]);

        return (
            <Box flexDirection="row-reverse">
                {appendText ? (
                    <Box>
                        <AnimatedText {...textProps} style={animatedTextStyle}>
                            {appendText}
                        </AnimatedText>
                    </Box>
                ) : null}
                <Box alignItems={alignText}>
                    <Box overflow={'hidden'} height={lineHeight}>
                        <Animated.View style={animatedTransformStyle}>
                            <AnimatedText {...textProps} textAlign={textAlignValue} style={animatedTextStyle}>
                                {formattedDisplayValue}
                            </AnimatedText>
                        </Animated.View>
                    </Box>
                </Box>
                {prependText ? (
                    <Box>
                        <AnimatedText {...textProps} style={animatedTextStyle}>
                            {prependText}
                        </AnimatedText>
                    </Box>
                ) : null}
            </Box>
        );
    }
);
